import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerApi, metaApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Spinner from '../../components/Spinner.jsx';
import Badge from '../../components/Badge.jsx';
import { TextInput } from '../../components/Field.jsx';
import { formatCurrency } from '../../utils/format.js';

/**
 * The pipeline board.
 *
 * Same screen for teleprospection and sales - what differs is which columns are
 * shown and which ones you may drop a card into. Both come from the session
 * (`user.pipeline`), which the API derives from config/pipeline.js, so the board
 * can never disagree with what the server will accept.
 *
 * A card can be dragged, or moved with the arrows on small screens where drag
 * and drop is not usable.
 */
const PipelinePage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();

  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const { data, loading, refresh, setData } = useApi(() => customerApi.list({ pageSize: 200 }), []);
  const { data: meta } = useApi(() => metaApi.get(), []);

  const locale = i18n.resolvedLanguage;
  const visibleStages = user.pipeline?.visible ?? [];
  const editableStages = user.pipeline?.editable ?? [];
  const ownerOf = useMemo(() => {
    const map = new Map((meta?.pipeline ?? []).map((stage) => [stage.key, stage.owner]));
    return (key) => map.get(key) ?? null;
  }, [meta]);

  const customers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data?.items ?? []).filter(
      (customer) => !needle || customer.companyName.toLowerCase().includes(needle),
    );
  }, [data, search]);

  const move = async (customer, status) => {
    if (customer.status === status) return;
    if (!editableStages.includes(status)) {
      toast.error(t('pipeline.notYours', { role: t(`roles.${ownerOf(status)}`) }));
      return;
    }

    // Optimistic: the card moves immediately, and rolls back if the API says no.
    const previous = data;
    setData((current) => ({
      ...current,
      items: current.items.map((row) => (row.id === customer.id ? { ...row, status } : row)),
    }));

    try {
      await customerApi.update(customer.id, { status });
      toast.success(t('pipeline.moved', { name: customer.companyName, stage: t(`customers.status.${status}`) }));
    } catch (err) {
      setData(previous);
      toast.error(toMessage(err));
      refresh();
    }
  };

  if (loading) return <Spinner full />;

  const total = customers.reduce((sum, customer) => sum + (customer.estimatedValue ?? 0), 0);

  return (
    <>
      <PageHeader
        title={t('nav.pipeline')}
        subtitle={`${customers.length} ${t('common.results')} - ${formatCurrency(total, locale)}`}
      />

      <Toolbar>
        <TextInput value={search} onChange={setSearch} placeholder={t('common.search')} />
        <span className="muted small">{t('pipeline.dragHint')}</span>
      </Toolbar>

      <div className="kanban">
        {visibleStages.map((status) => {
          const column = customers.filter((customer) => customer.status === status);
          const columnValue = column.reduce((sum, customer) => sum + (customer.estimatedValue ?? 0), 0);
          const canDrop = editableStages.includes(status);
          const owner = ownerOf(status);

          return (
            <section
              key={status}
              className={[
                'kanban__column',
                canDrop ? '' : 'kanban__column--locked',
                dropTarget === status && canDrop ? 'kanban__column--over' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onDragOver={(event) => {
                if (!canDrop) return;
                event.preventDefault();
                setDropTarget(status);
              }}
              onDragLeave={() => setDropTarget((current) => (current === status ? null : current))}
              onDrop={(event) => {
                event.preventDefault();
                setDropTarget(null);
                if (dragging) move(dragging, status);
                setDragging(null);
              }}
            >
              <header className="kanban__header">
                <span>{t(`customers.status.${status}`)}</span>
                <span className="kanban__count">{column.length}</span>
              </header>

              <p className="kanban__value">{formatCurrency(columnValue, locale)}</p>

              {/* Who owns this stage - and therefore who may move a deal into it */}
              <p className="kanban__owner">
                {owner ? (
                  <Badge value={owner} label={t(`roles.${owner}`)} />
                ) : (
                  <span className="muted small">{t('pipeline.anyone')}</span>
                )}
                {!canDrop ? <span className="kanban__lock" title={t('pipeline.readOnly')}>&#128274;</span> : null}
              </p>

              <div className="kanban__cards">
                {column.map((customer) => {
                  const index = visibleStages.indexOf(status);
                  const prev = visibleStages[index - 1];
                  const next = visibleStages[index + 1];

                  return (
                    <article
                      key={customer.id}
                      className="kanban__card"
                      draggable={editableStages.length > 0}
                      onDragStart={() => setDragging(customer)}
                      onDragEnd={() => {
                        setDragging(null);
                        setDropTarget(null);
                      }}
                    >
                      <button
                        type="button"
                        className="kanban__card-main"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        <span className="kanban__card-title">{customer.companyName}</span>
                        <span className="kanban__card-meta">
                          {[customer.contactFirstName, customer.contactLastName].filter(Boolean).join(' ') || '-'}
                        </span>
                        <span className="kanban__card-value">
                          {formatCurrency(customer.estimatedValue, locale, customer.currency)}
                        </span>
                      </button>

                      {/* Keyboard- and touch-friendly alternative to dragging */}
                      <div className="kanban__card-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          disabled={!prev || !editableStages.includes(prev)}
                          onClick={() => move(customer, prev)}
                          aria-label={t('common.previous')}
                          title={prev ? t(`customers.status.${prev}`) : ''}
                        >
                          &lsaquo;
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          disabled={!next || !editableStages.includes(next)}
                          onClick={() => move(customer, next)}
                          aria-label={t('common.next')}
                          title={next ? t(`customers.status.${next}`) : ''}
                        >
                          &rsaquo;
                        </button>
                      </div>
                    </article>
                  );
                })}

                {!column.length ? <p className="kanban__empty">{t('common.noData')}</p> : null}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
};

export default PipelinePage;
